import { faEye } from "@jewlr/pro-light-svg-icons/faEye"
import { faEyeSlash } from "@jewlr/pro-light-svg-icons/faEyeSlash"
import { faTimesCircle } from "@jewlr/pro-solid-svg-icons/faTimesCircle"
import { cva } from "class-variance-authority"
import PropTypes from "prop-types"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

import { cn } from "styles/utils"

import Button from "./button"
import FontAwesomeIcon from "./font-awesome-icon"

const inputWrapperVariants = cva("relative flex", {
  compoundVariants: [
    {
      class: "border-error border-2",
      error: true,
      focused: true,
    },
  ],
  defaultVariants: {
    disabled: false,
    error: false,
    focused: false,
    variant: "default",
  },
  variants: {
    disabled: {
      false: "bg-white",
      true: "bg-grey-4",
    },
    error: {
      false: null,
      true: "border-error border-2",
    },
    focused: {
      false: null,
      true: "ring-3 ring-primary border-black",
    },
    variant: {
      checkout: "border-grey-30 rounded-md border",
      default: "border-grey-10 border-2",
    },
  },
})

const labelVariants = cva(
  "text-12 pointer-events-none absolute top-12 inline-block transition-all duration-200",
  {
    compoundVariants: [
      {
        class: "top-6",
        float: true,
        variant: "checkout",
      },
      {
        class: "top-7",
        float: true,
        variant: "default",
      },
      {
        class: "text-grey-30",
        disabled: true,
        variant: "default",
      },
    ],
    defaultVariants: {
      disabled: false,
      float: false,
      variant: "default",
    },
    variants: {
      disabled: {
        false: "",
        true: "",
      },
      float: {
        false: "opacity-0",
        true: "opacity-100",
      },
      rtl: {
        false: "",
        true: "right-0",
      },
      variant: {
        checkout: "px-12 leading-10 text-black",
        default: "text-grey-65 px-8 leading-none",
      },
    },
  }
)

const inputFieldVariants = cva(
  "h-42 placeholder:text-grey-50 disabled:text-grey-30 outline-hidden focus:outline-hidden w-full max-w-full flex-1 border-none bg-white text-black transition-all duration-200 focus:transition-none",
  {
    compoundVariants: [
      {
        class: "pb-0 pt-14",
        float: true,
        variant: "checkout",
      },
      {
        class: "pb-0 pt-16",
        float: true,
        variant: "default",
      },
      {
        class: "pr-56",
        float: true,
        rtl: false,
        withAction: true,
      },
      {
        class: "pl-56",
        float: true,
        rtl: true,
        withAction: true,
      },
    ],
    defaultVariants: {
      float: false,
      variant: "default",
    },
    variants: {
      float: {
        false: "",
        true: "",
      },
      rtl: {
        false: "",
        true: "placeholder:text-right",
      },
      variant: {
        checkout: "text-16 leading-24 rounded-md p-12",
        default: "leading-16 text-14 p-8",
      },
      withAction: {
        false: "",
        true: "",
      },
    },
  }
)

const fieldErrorVariants = cva("text-error", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      checkout: "text-14 leading-20 mt-6",
      default: "text-12 mt-8",
    },
  },
})

const FloatInput = forwardRef(
  (
    {
      className,
      containerClassName,
      disabled,
      error,
      errorClassName,
      id,
      inputWrapperClassName,
      label,
      labelClassName,
      name,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onReturn,
      placeholder,
      rtl = false,
      showClear,
      type,
      value,
      variant,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const internalInputRef = useRef()

    useImperativeHandle(ref, () => internalInputRef.current)

    const float = !!value && !!label

    const handleKeyDown = useCallback(
      (e) => {
        if (onReturn && e.key === "Enter") {
          e.preventDefault()
          onReturn(e)
        }
        if (onKeyDown) onKeyDown(e)
      },
      [onKeyDown, onReturn]
    )

    const handleBlur = useCallback(
      (e) => {
        setFocused(false)
        if (onBlur) onBlur(e)
      },
      [onBlur]
    )

    const handleFocus = useCallback(
      (e) => {
        setFocused(true)
        if (onFocus) onFocus(e)
      },
      [onFocus]
    )

    const handleClear = useCallback(
      (e) => {
        if (onChange) {
          onChange({
            ...e,
            target: {
              ...e.target,
              value: "",
            },
          })
          internalInputRef.current?.focus()
        }
      },
      [onChange]
    )

    return (
      <div className={cn("text-left", containerClassName)}>
        <div
          className={cn(
            inputWrapperVariants({
              className: inputWrapperClassName,
              disabled,
              error: !!error,
              focused,
              variant,
            })
          )}
        >
          {label && (
            <label
              className={cn(
                labelVariants({
                  className: labelClassName,
                  disabled,
                  float,
                  rtl,
                  variant,
                })
              )}
              htmlFor={id || name}
            >
              {label}
            </label>
          )}
          <input
            aria-invalid={!!error}
            className={cn(
              inputFieldVariants({
                className,
                float,
                rtl,
                variant,
                withAction: showClear || type === "password",
              })
            )}
            disabled={disabled}
            id={id || name}
            name={name}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || label}
            ref={internalInputRef}
            type={
              type === "password" ? (showPassword ? "text" : "password") : type
            }
            value={value}
            {...props}
          />
          <div
            className={cn(
              "absolute top-1/2 -translate-y-1/2 items-center",
              float ? "flex" : "hidden",
              rtl ? "left-8" : "right-8"
            )}
          >
            {showClear && (
              <Button
                aria-label="Clear Text"
                className="text-18 text-grey-20 flex items-center px-12"
                onClick={handleClear}
                tabIndex="-1"
                text
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </Button>
            )}
            {type === "password" && (
              <Button
                aria-label={showPassword ? "Hide Password" : "Show Password"}
                aria-pressed={showPassword}
                className="rounded-full"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon
                  className="text-16 text-grey-30"
                  icon={showPassword ? faEyeSlash : faEye}
                />
              </Button>
            )}
          </div>
        </div>
        {error && (
          <p
            className={cn(
              fieldErrorVariants({ className: errorClassName, variant })
            )}
          >
            {error.message || error}
          </p>
        )}
      </div>
    )
  }
)

FloatInput.displayName = "FloatInput"

FloatInput.propTypes = {
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  errorClassName: PropTypes.string,
  id: PropTypes.string,
  inputWrapperClassName: PropTypes.string,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onReturn: PropTypes.func,
  placeholder: PropTypes.string,
  rtl: PropTypes.bool,
  showClear: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(["default", "checkout"]),
}

FloatInput.defaultProps = {
  showClear: false,
  variant: "default",
}

export default FloatInput
